/** Small bounded packets; avoid building a string one byte at a time. */
export function pcmToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 8192) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(offset, offset + 8192) as unknown as number[]
    );
  }
  return btoa(binary);
}

// Self-contained worklet code so tests exercise exactly the processor sent to
// the audio thread. Buffers are transferred back after encoding for reuse.
export const PCM_WORKLET_SOURCE = String.raw`
class NovaPcmProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.pool = [];
    this.packet = new Int16Array(2048);
    this.offset = 0;
    this.samples = 0;
    this.inFlight = 0;
    this.limited = false;
    this.port.onmessage = ({data}) => {
      if (data.type === 'recycle') {
        this.inFlight = Math.max(0, this.inFlight - 1);
        if (data.buffer.byteLength === 4096 && this.pool.length < 3) this.pool.push(data.buffer);
      } else if (data.type === 'flush') {
        this.flush();
        this.limited = true;
        this.port.postMessage({type: 'flushed'});
      }
    };
  }
  flush() {
    if (!this.offset || this.limited) return;
    // Bound queued audio if the UI thread cannot keep up. Stop visibly rather
    // than silently dropping samples or building an unbounded message queue.
    if (this.inFlight >= 32) {
      this.limited = true;
      this.port.postMessage({type: 'overflow'});
      return;
    }
    this.inFlight++;
    const buffer = this.offset === 2048 ? this.packet.buffer : this.packet.buffer.slice(0, this.offset * 2);
    this.port.postMessage({type: 'audio', buffer}, [buffer]);
    this.packet = new Int16Array(this.pool.pop() || new ArrayBuffer(4096));
    this.offset = 0;
  }
  process(inputs) {
    const input = inputs[0]?.[0];
    if (!input || this.limited) return true;
    for (let index = 0; index < input.length; index++) {
      const sample = Math.max(-1, Math.min(1, input[index]));
      this.packet[this.offset++] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      this.samples++;
      if (this.offset === 2048) this.flush();
      if (this.limited) return true;
      // Enforce the existing one-minute cap on the audio thread as well, even
      // when a busy UI thread delays the normal recording timer.
      if (this.samples >= 16000 * 60) {
        this.flush();
        this.limited = true;
        this.port.postMessage({type: 'limit'});
        break;
      }
    }
    return true;
  }
}
registerProcessor('nova-pcm-processor', NovaPcmProcessor);
`;
